// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect, useRef } from 'react';
import { formatDate } from '@loa/theme';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Spinner, Button, Dropdown, TablePagination, TextInput, TagChipGroup, TagFilterSelect } from '@loa/ui-components';
import { clientListApi } from '../api/clientListApi';
import { relationshipApi } from '../api/relationshipApi';
import { StatusBadge } from '../components/StatusBadge';
import { ViewToggle } from '../components/ViewToggle';
import { AdvisorFilter } from '../components/AdvisorFilter';
import { useAdminViewToggle } from '../hooks/useAdminViewToggle';
import { useTags } from '../contexts/TagsContext';
import { useEntityTags } from '../hooks/useEntityTags';
import type { ClientListDTO, Page } from '../types/clientList';

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function ClientsListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Data state
  const [data, setData] = useState<Page<ClientListDTO> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch relationships for dropdown
  const [relationships, setRelationships] = useState<Array<{ key: string; text: string; value: string }>>([]);

  // Admin view toggle hook
  const {
    currentView,
    setCurrentView,
    isAdminView,
    showToggle,
    isInitializing
  } = useAdminViewToggle({
    storageKey: 'clients',
    syncWithUrl: true
  });

  // State for advisor filter (only used in admin view)
  const [advisorFilter, setAdvisorFilter] = useState<string>('all');

  // Tag filter (Tags PRD FR-13, ANY-of).
  const { tags: allTags, featureEnabled: tagsFeatureEnabled } = useTags();
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([]);

  // Ref to track if this is the initial load (to avoid double-loading on mount)
  const isInitialLoad = useRef(true);

  // Load relationships for dropdown
  useEffect(() => {
    const loadRelationships = async () => {
      try {
        // In admin view, load all relationships; otherwise load only user's relationships
        const response = isAdminView
          ? await relationshipApi.getAllRelationships(0, 100)
          : await relationshipApi.getRelationships({ page: 0, size: 100 });
        const relationshipOptions = response.content.map(r => ({
          key: r.id,
          text: r.profileName || 'Unnamed Relationship',
          value: r.id,
        }));
        setRelationships([
          { key: 'all', text: 'All Relationships', value: '' },
          ...relationshipOptions,
        ]);
      } catch (err) {
        console.error('Failed to load relationships:', err);
      }
    };

    loadRelationships();
  }, [isAdminView]);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters = {
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        profileId: relationshipFilter || undefined,
        sortBy: sortColumn || undefined,
        sortDirection: sortColumn ? sortDirection : undefined,
        page: currentPage,
        size: pageSize,
        // Admin view: fetch all clients across the brokerage
        all: isAdminView || undefined,
        // Filter by specific advisor (only in admin view)
        advisorId: isAdminView && advisorFilter !== 'all' ? advisorFilter : undefined,
        tagIds: tagFilterIds.length > 0 ? tagFilterIds : undefined,
      };

      const response = await clientListApi.getClients(filters);
      setData(response);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, statusFilter, relationshipFilter, sortColumn, sortDirection, currentPage, pageSize, isAdminView, advisorFilter, tagFilterIds]);

  // Load clients when filters change
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Reset to page 0 when filters change (except currentPage itself)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter, relationshipFilter, advisorFilter, tagFilterIds]);

  // Reset filters, pagination, and selection when view changes
  useEffect(() => {
    // Skip on initial load to avoid double-loading
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // Reset all filters and pagination
    setCurrentPage(0);
    setSearchQuery('');
    setStatusFilter('');
    setRelationshipFilter('');
    setAdvisorFilter('all');
    setSortColumn('');
    setSortDirection('asc');
    setTagFilterIds([]);
  }, [currentView]);

  const handleSort = useCallback((column: string) => {
    if (column !== 'name' && column !== 'lastActivity') {
      return; // Only name and lastActivity are sortable
    }

    if (sortColumn === column) {
      // Toggle direction
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Remove sort
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  const renderSortArrow = (column: string): string => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const clients = data?.content || [];
  const totalPages = data?.totalPages || 0;
  const totalElements = data?.totalElements || 0;

  // Batch tag decoration — ONE call per page of rows (never per-row).
  const clientIds = clients.map((c) => c.id);
  const { tagsByEntityId } = useEntityTags('CLIENT', clientIds);

  // Compute the number of columns for colSpan
  const columnCount = (isAdminView ? 9 : 8) + (tagsFeatureEnabled ? 1 : 0);

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: '1.5em', fontSize: '1.1em', color: '#666' }}>
        <Link to="/" style={{ color: '#4169E1', textDecoration: 'none' }}>
          <AppIcon name="home" /> Dashboard
        </Link>
        {' / '}
        <span>Clients</span>
      </nav>

      {/* Page Header */}
      <div style={{ marginBottom: '1em' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
          <AppIcon name="users" />
          Clients
          <span style={{ marginLeft: '0.5em', background: '#2185d0', color: 'white', borderRadius: '50%', padding: '2px 8px', fontSize: '0.75em' }}>
            {totalElements}
          </span>
        </h2>
        <p style={{ color: '#666', margin: '0.5em 0 0 0' }}>
          {isAdminView ? 'All clients across the brokerage' : 'All clients across your portfolio'}
        </p>
      </div>

      {/* View Toggle - Only shown for admin users */}
      {showToggle && (
        <div style={{ marginBottom: '1em' }}>
          <ViewToggle
            currentView={currentView}
            onViewChange={setCurrentView}
            disabled={isInitializing || isLoading}
          />
        </div>
      )}

      {/* Search and Filters */}
      <div style={{ padding: '1rem', background: '#f9f9f9', borderRadius: '4px', marginBottom: '1em' }}>
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Advisor Filter - First position in admin view */}
          {isAdminView && (
            <div style={{ flex: '1 1 180px' }}>
              <AdvisorFilter
                value={advisorFilter}
                onChange={setAdvisorFilter}
                disabled={isLoading}
                placeholder="Filter by Advisor"
              />
            </div>
          )}
          <div style={{ flex: '2 1 200px' }}>
            <TextInput
              iconLeft={<AppIcon name="search" size={16} color="#333333" />}
              placeholder="Search by name, email, or CRM number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fluid
            />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Dropdown
              placeholder="Filter by status"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              options={[
                { text: 'All Statuses', value: '' },
                { text: 'Draft', value: 'DRAFT' },
                { text: 'Invited', value: 'INVITED' },
                { text: 'Pending Signature', value: 'PENDING_SIGNATURE' },
                { text: 'Signed', value: 'SIGNED' },
                { text: 'Inactive', value: 'INACTIVE' },
                { text: 'Archived', value: 'ARCHIVED' },
              ]}
            />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Dropdown
              placeholder="Filter by relationship"
              value={relationshipFilter}
              onChange={(value) => setRelationshipFilter(value)}
              options={relationships.map(r => ({ value: r.value, text: r.text }))}
              search
            />
          </div>
          {tagsFeatureEnabled && (
            <TagFilterSelect
              options={allTags.filter((t) => !t.archived).map((t) => ({ id: t.id, name: t.name, color: t.color }))}
              selectedIds={tagFilterIds}
              onChange={setTagFilterIds}
              disabled={isLoading}
              data-testid="clients-tag-filter"
            />
          )}
        </div>
      </div>

      {/* Clients Table */}
      <div style={{ background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0', padding: '1rem' }}>
        <table className="fin-table">
          <thead>
            <tr>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('name')}
              >
                Client Name {renderSortArrow('name')}
              </th>
              <th>Email</th>
              <th>Status</th>
              {isAdminView && <th>Advisor</th>}
              <th>Relationship</th>
              <th>CRM Person #</th>
              <th style={{ textAlign: 'center' }}>Policies (Owner / Signatory)</th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('lastActivity')}
              >
                Last Activity {renderSortArrow('lastActivity')}
              </th>
              {tagsFeatureEnabled && <th>Tags</th>}
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={columnCount} style={{ textAlign: 'center' }}>
                  <Spinner />
                </td>
              </tr>
            )}

            {error && (
              <tr>
                <td colSpan={columnCount}>
                  <Alert variant="error" header="Error Loading Clients">
                    <p>{error.message || 'Failed to load clients'}</p>
                    <Button variant="filled" size="sm" onClick={loadClients}>Retry</Button>
                    {isAdminView && (
                      <Button variant="outlined" size="sm" onClick={() => setCurrentView('my-data')} style={{ marginLeft: '0.5em' }}>
                        Switch to My Data
                      </Button>
                    )}
                  </Alert>
                </td>
              </tr>
            )}

            {!isLoading && !error && clients.length === 0 && (
              <tr>
                <td colSpan={columnCount}>
                  <Alert variant="info" header="No Clients Found">
                    <p>
                      {searchQuery || statusFilter || relationshipFilter || (isAdminView && advisorFilter !== 'all')
                        ? 'No clients match your search criteria. Try adjusting your filters.'
                        : isAdminView
                          ? 'No clients exist in the brokerage.'
                          : 'No clients have been added yet. Create a relationship and add clients to get started.'}
                    </p>
                  </Alert>
                </td>
              </tr>
            )}

            {!isLoading && !error && clients.map((client) => (
              <tr
                key={client.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/clients/${client.id}`)}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#666',
                        flexShrink: 0,
                      }}
                    >
                      {client.firstName[0]}{client.lastName[0]}
                    </div>
                    <div>
                      <strong>{client.firstName} {client.lastName}</strong>
                      {client.isPrimary && (
                        <span style={{ marginLeft: '0.5em', fontSize: '0.75em', background: '#2185d0', color: 'white', padding: '1px 5px', borderRadius: '3px' }}>
                          <AppIcon name="star" /> Primary
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{client.email}</td>
                <td>
                  <StatusBadge status={client.status} type="client" showIcon={true} showTooltip={true} />
                </td>
                {isAdminView && (
                  <td>
                    {client.advisorName || <em style={{ color: '#999' }}>Unassigned</em>}
                  </td>
                )}
                <td>
                  <Link to={`/relationships/${client.profileId}`} onClick={(e) => e.stopPropagation()}>
                    {client.profileName || <em style={{ color: 'grey' }}>Unnamed Relationship</em>}
                  </Link>
                </td>
                <td>{client.crmPersonNumber || <em style={{ color: 'grey' }}>-</em>}</td>
                <td style={{ textAlign: 'center' }}>
                  <span
                    title={`Owner: ${client.ownerPolicyCount} policies | Signatory: ${client.signatoryPolicyCount} policies`}
                    style={{ padding: '2px 8px', background: '#e0e0e0', borderRadius: '3px', cursor: 'default' }}
                  >
                    {client.ownerPolicyCount} / {client.signatoryPolicyCount}
                  </span>
                </td>
                <td>{formatDate(client.lastActivity)}</td>
                {tagsFeatureEnabled && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <TagChipGroup
                      tags={(tagsByEntityId[client.id] ?? []).map((t) => ({
                        id: t.id, name: t.name, color: t.color, archived: t.archived,
                      }))}
                    />
                  </td>
                )}
                <td style={{ textAlign: 'center' }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <Button variant="outlined" size="sm" onClick={() => navigate(`/clients/${client.id}`)}>
                    <AppIcon name="eye" /> View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {totalPages > 0 && (
          <TablePagination
            config={{
              currentPage: currentPage + 1,
              totalPages,
              itemsPerPage: pageSize,
              itemsPerPageOptions: [10, 20, 50],
              onPageChange: (next) => setCurrentPage(next - 1),
              onItemsPerPageChange: (size) => {
                setPageSize(size);
                setCurrentPage(0);
              },
            }}
          />
        )}
      </div>
    </div>
  );
}
